import React, { useEffect, useState } from "react";
import cn from "classnames";
import { ToggleButton } from "../toggle-button/toggle-button";
import IconTranslate from "@src/assets/svg/icon-translate.svg";
import styles from "./auto-translation-button.module.scss";
import { useI18N } from "@src/language/language-context";
import { patchTicketAutoTranslationUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-auto-translation-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

const cssClassName = "auto-translation-button";

interface AutoTranslationButtonProps {
  ticketId: string | null;
  backgroundColor?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  ticketAutoTranslationActive?: boolean;
  callbackForRefresh?: () => void | Promise<void>;
}

export default function AutoTranslationButton({
  ticketId,
  backgroundColor,
  disabled = false,
  disabledTooltip = "",
  ticketAutoTranslationActive = false,
  callbackForRefresh,
}: AutoTranslationButtonProps) {
  const i18n = useI18N();
  const texts = {
    autoTranslationLabel: i18n(
      "modules.autoTranslationButton.autoTranslationLabel",
    ),
    settingsUpdatedSuccessfully: i18n(
      "modules.autoTranslationButton.settingsUpdatedSuccessfully",
    ),
  };

  const [isAutoTranslationEnabled, setIsAutoTranslationEnabled] = useState(
    ticketAutoTranslationActive,
  );
  const [isLoading, setIsLoading] = useState(false);

  // Re-sync (not remount) when the server value changes mid-refresh — a key-based
  useEffect(() => {
    setIsAutoTranslationEnabled(ticketAutoTranslationActive);
  }, [ticketAutoTranslationActive]);

  const handleAutoTranslationToggle = async () => {
    const nextValue = !isAutoTranslationEnabled;
    setIsAutoTranslationEnabled(nextValue);
    setIsLoading(true);
    try {
      const response = await patchTicketAutoTranslationUseCase(
        nextValue,
        ticketId,
      );

      if (response.success) {
        successSnackbar(texts.settingsUpdatedSuccessfully);
        await callbackForRefresh?.();
      } else {
        console.error(
          "Failed to update auto-translation setting:",
          response.error,
        );
        errorSnackbar(response.error);
        setIsAutoTranslationEnabled(!nextValue);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--background`]]:
          backgroundColor === "background",
      })}
      title={disabled ? disabledTooltip : undefined}
    >
      <img src={IconTranslate} alt="Translation icon" />
      <span className={styles[`${cssClassName}__translation-label`]}>
        {texts.autoTranslationLabel}
      </span>
      <ToggleButton
        isActive={isAutoTranslationEnabled}
        handleClick={handleAutoTranslationToggle}
        disabled={disabled || isLoading}
      />
    </div>
  );
}
